import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikAutocompleteInput,
  FormikDateInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import { RNHFeriadoSerieUpd } from "services/services-mia/programa-salud/feriados/RNHFeriadoSerieUpd";
import { RNHFeriadoSerieGet } from "services/services-mia/programa-salud/feriados/RNHFeriadoSerieGet";
import { RNHFeriadoGet } from "services/services-mia/programa-salud/feriados/RNHFeriadoGet";

/**
 * Modificación de UNA fecha: se puede corregir el día o mover la fecha a otro feriado.
 */
export default function UpdateModal({ row, closeModal, updateList }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [data, setData] = useState();
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  const [feriados, setFeriados] = useState([]);
  const [cargandoFeriados, setCargandoFeriados] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: result } = await RNHFeriadoGet(credentials);
      setFeriados(result?.[0]?.rnhFeriadoId ? result : []);
      setCargandoFeriados(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se relee el registro aunque la fila ya traiga todo: lo que importa es la VERSION.
  // Si alguien mas lo modifico desde que se cargo la grilla, la de la fila quedo vieja
  // y el Upd lo rechaza.
  const getSerieData = async () => {
    setInRequest(true);
    const { data: result } = await RNHFeriadoSerieGet(credentials, { id: row.id });
    const encontrado = Array.isArray(result)
      ? result.find((s) => String(s.rnhFeriadoSerieId) === String(row.id))
      : null;
    setInRequest(false);

    if (!encontrado) {
      setSnackBarInfo({
        message: "No se pudo cargar la fecha seleccionada.",
        severity: "error",
        open: true,
      });
      closeModal();
      return;
    }

    formikRef.current.setValues({
      // Sin String(): FormikAutocompleteInput normaliza con String() de los dos
      // lados, asi que un id numerico del back matchea igual con su opcion.
      //
      // El nombre sale de la REGLA del back, no del param del Upd: ver la nota en
      // index.jsx. Leerlo como rnhFeriadoSerieFeriadoId daba undefined y el buscador
      // abria vacio aunque la fecha si se precargaba.
      feriadoId:
        encontrado.rnhFeriadoId ?? encontrado.rnhFeriadoSerieFeriadoId ?? "",
      fecha: encontrado.rnhFeriadoSerieFecha ?? "",
    }, false);
    setData(encontrado);
  };

  const guardarFecha = async (values) => {
    setInRequest(true);
    const response = await RNHFeriadoSerieUpd(credentials, {
      // Lo recien leido manda; la fila de la grilla es el respaldo por si el Get fallo.
      feriadoSerieId: data?.rnhFeriadoSerieId ?? row.id,
      feriadoId: values.feriadoId,
      fecha: values.fecha,
      version: data?.rnhFeriadoSerieVersion ?? row.version,
    });
    setInRequest(false);

    if (!response.ok) return;

    setSnackBarInfo({
      message: "Fecha modificada correctamente",
      severity: "success",
      open: true,
    });
    formikRef.current.resetForm();
    updateList();
    closeModal();
  };

  useEffect(() => {
    getSerieData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        feriadoId: row?.feriadoId ?? "",
        fecha: row?.fecha ?? "",
      }}
      validationSchema={yup.object({
        feriadoId: yup.string().required("Debe elegir un feriado"),
        fecha: yup.string().required("Debe ingresar una fecha"),
      })}
      onSubmit={(values) => {
        guardarFecha(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div className={styles["column"]}>
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            <Grid item xs={8}>
              {/* Con buscador y no un <select> comun: la lista de feriados crece
                  sola y encontrar uno escribiendo es mas rapido que recorrerla.
                  No lleva opcion "Seleccione": el placeholder "Buscar..." del
                  componente ya dice que hacer, y el campo arranca vacio. */}
              <FormikAutocompleteInput
                name="feriadoId"
                labelText="Feriado"
                options={feriados}
                optionValue="rnhFeriadoId"
                optionLabel="rnhFeriadoNombre"
                disabled={cargandoFeriados}
              />
            </Grid>
            <Grid item xs={4}>
              <FormikDateInput name="fecha" labelText="Fecha" />
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest || cargandoFeriados}
            newFormData={{ button: "Modificar" }}
            onCloseModal={closeModal}
          />
          {(inRequest || cargandoFeriados) && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
